import { useCallback } from 'react';
import { Dialog, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import CitationPanel, { CitationProps } from './CitationPanel';
import { useActionPopoverControl } from 'op-contexts';

interface CitationOverlayProps {
  open: boolean;
  citations: CitationProps[];
  activeIndex: number;
  onClose: () => void;
  onActiveIndexChange: (nextIndex: number) => void;
}

const CitationOverlay = ({
  open,
  citations,
  activeIndex,
  onClose,
  onActiveIndexChange,
}: CitationOverlayProps): JSX.Element | null => {
  const totalCitations = citations.length;
  const clampedIndex = Math.min(Math.max(activeIndex, 0), totalCitations - 1);
  const hasMultipleCitations = totalCitations > 1;
  const { dialogProps: actionPopoverDialogProps } = useActionPopoverControl({ open, enableActionPopover: true });

  const handleNavigatePrev = useCallback(() => {
    if (!hasMultipleCitations) return;
    const nextIndex = (clampedIndex - 1 + totalCitations) % totalCitations;
    onActiveIndexChange(nextIndex);
  }, [clampedIndex, hasMultipleCitations, onActiveIndexChange, totalCitations]);

  const handleNavigateNext = useCallback(() => {
    if (!hasMultipleCitations) return;
    const nextIndex = (clampedIndex + 1) % totalCitations;
    onActiveIndexChange(nextIndex);
  }, [clampedIndex, hasMultipleCitations, onActiveIndexChange, totalCitations]);

  if (!open || totalCitations === 0) return null;

  const citationContent = citations[clampedIndex];
  const isLetterCanvas = citationContent?.documentType === 'letter';

  const headerContent = (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {hasMultipleCitations && !isLetterCanvas && (
        <>
          <IconButton
            onClick={handleNavigatePrev}
            size="small"
            aria-label="Previous citation"
            color="inherit"
            disabled={!hasMultipleCitations}>
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={handleNavigateNext}
            size="small"
            aria-label="Next citation"
            color="inherit"
            disabled={!hasMultipleCitations}>
            <ChevronRight />
          </IconButton>
        </>
      )}
      <Typography variant="h6" fontWeight={500} color="inherit">
        {isLetterCanvas ? 'RO-Consult' : 'Citations'}
      </Typography>
      {hasMultipleCitations && !isLetterCanvas && (
        <Typography variant="body2" color="inherit">
          {clampedIndex + 1} / {totalCitations}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      keepMounted
      container={document.body}
      {...actionPopoverDialogProps}
      PaperProps={{
        sx: {
          m: 0,
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          backgroundColor: 'grey.900',
          color: 'common.white',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'grey.900',
          },
        },
      }}>
      <CitationPanel citation={citationContent} onClose={onClose} headerContent={headerContent} variant="overlay" />
    </Dialog>
  );
};

export default CitationOverlay;
